import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../services/supabaseClient";

// LOT0F11_ADMIN_MERCHANT_ONBOARDING_REVIEW_V1

type AnyRecord = Record<string, any>;
type Decision = "approve" | "reject" | "suspend";
type MerchantRequest = AnyRecord & {
  business?: AnyRecord | null;
  profile?: AnyRecord | null;
  prp?: AnyRecord | null;
  linked_lead?: AnyRecord | null;
};

const text = (value: unknown, fallback = "") =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

const statusOf = (row: MerchantRequest) =>
  text(row.status, "under_review").toLowerCase();

async function invokeReview(body: Record<string, unknown>) {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const token = session?.access_token?.trim();
  if (!token) throw new Error("admin_session_required");

  const result = await supabase.functions.invoke("admin-merchant-onboarding-review", {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });

  if (result.error) {
    const payload = result.data as AnyRecord | null;
    throw new Error(
      typeof payload?.error === "string"
        ? payload.error
        : result.error.message || "admin_merchant_review_failed",
    );
  }

  const payload = (result.data || {}) as AnyRecord;
  if (payload.ok !== true) {
    throw new Error(text(payload.error, "admin_merchant_review_failed"));
  }
  return payload;
}

export default function AdminMerchantOnboardingReviewPanel() {
  const [requests, setRequests] = useState<MerchantRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "under_review" | "approved" | "rejected" | "suspended">("under_review");
  const [regionFilter, setRegionFilter] = useState("all");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<Decision | "">("");
  const [lastResult, setLastResult] = useState<AnyRecord | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await invokeReview({ action: "list" });
      setRequests(Array.isArray(payload.requests) ? payload.requests : []);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "merchant_onboarding_list_failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const regions = useMemo(() => Array.from(new Set(
    requests.map((row) => text(row.region || row.business?.region || row.prp?.region)).filter(Boolean),
  )).sort((a, b) => a.localeCompare(b, "fr")), [requests]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((row) => {
      if (statusFilter !== "all" && statusOf(row) !== statusFilter) return false;
      const region = text(row.region || row.business?.region || row.prp?.region);
      if (regionFilter !== "all" && region !== regionFilter) return false;
      if (!query) return true;
      return [
        row.business_name, row.contact_name, row.contact_email, row.contact_phone,
        row.business?.name, row.profile?.full_name, row.profile?.email,
        row.prp?.full_name, row.prp?.email, region,
      ].filter(Boolean).join(" ").toLowerCase().includes(query);
    });
  }, [requests, search, statusFilter, regionFilter]);

  const selected = useMemo(
    () => requests.find((row) => text(row.id) === selectedId) || null,
    [requests, selectedId],
  );

  const counts = useMemo(() => ({
    review: requests.filter((row) => statusOf(row) === "under_review").length,
    approved: requests.filter((row) => statusOf(row) === "approved").length,
    attention: requests.filter((row) => ["rejected", "suspended"].includes(statusOf(row))).length,
  }), [requests]);

  const review = async (decision: Decision) => {
    if (!selected || busy) return;
    const businessId = text(selected.business_id);
    if (!businessId) return setError("business_id_missing");

    if (decision !== "approve" && reason.trim().length < 5) {
      return setError("Un motif d’au moins 5 caractères est requis.");
    }

    const label = decision === "approve"
      ? "approuver et activer le portail"
      : decision === "suspend"
        ? "suspendre le portail"
        : "refuser la demande";

    if (!window.confirm(`Confirmer : ${label} pour ${text(selected.business_name, "ce commerce")} ?`)) return;

    setBusy(decision);
    setError("");
    setLastResult(null);
    try {
      const result = await invokeReview({
        action: "review",
        business_id: businessId,
        decision,
        reason: reason.trim() || null,
        human_confirmation: true,
      });
      setLastResult(result);
      setReason("");
      await load();
      setSelectedId(selected.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "admin_merchant_review_failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-lot0f11-admin-merchant-review="true">
      <section className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-white to-emerald-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">0F-11 · Onboarding commerces</p>
            <h2 className="mt-1 text-2xl font-black text-slate-950">Validation finale du compte commerçant</h2>
            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
              La décision réutilise l’autorité SQL canonique existante. Une approbation active l’identité marchand et le portail Explore, jamais Stripe, Giftshop, un payout ou une autorité financière.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"><p className="text-2xl font-black text-amber-900">{counts.review}</p><p className="text-[10px] font-black uppercase text-amber-700">à valider</p></div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3"><p className="text-2xl font-black text-emerald-900">{counts.approved}</p><p className="text-[10px] font-black uppercase text-emerald-700">actifs</p></div>
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3"><p className="text-2xl font-black text-rose-900">{counts.attention}</p><p className="text-[10px] font-black uppercase text-rose-700">attention</p></div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(0,1fr)_190px_220px_auto]">
          <input className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold" placeholder="Commerce, contact, propriétaire, PRP…" value={search} onChange={(event) => setSearch(event.target.value)} />
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as any)}>
            <option value="under_review">À valider</option><option value="approved">Approuvés</option><option value="rejected">Refusés</option><option value="suspended">Suspendus</option><option value="all">Tous</option>
          </select>
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black" value={regionFilter} onChange={(event) => setRegionFilter(event.target.value)}>
            <option value="all">Toutes les régions</option>{regions.map((region) => <option key={region} value={region}>{region}</option>)}
          </select>
          <button className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm font-black text-emerald-800" onClick={() => void load()} type="button">Actualiser</button>
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800">{error}</div>}
      {lastResult && <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-semibold text-blue-900">Décision appliquée · Courriel: {lastResult.notification?.email_sent ? "envoyé" : "non confirmé"} · Pipeline PRP: {text(lastResult.prp_sync?.status, "non lié")}</div>}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_450px]">
        <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-5"><p className="font-black text-slate-900">Demandes commerçantes</p><p className="mt-1 text-xs font-semibold text-slate-400">{visible.length} dossier(s)</p></div>
          {loading ? <div className="p-10 text-center text-sm font-bold text-slate-400">Chargement…</div> : visible.length === 0 ? <div className="p-10 text-center text-sm font-bold text-slate-400">Aucun dossier.</div> : (
            <div className="divide-y divide-slate-100">{visible.map((row) => (
              <button className={`w-full p-5 text-left ${selectedId === text(row.id) ? "bg-emerald-50" : "hover:bg-slate-50"}`} key={text(row.id)} onClick={() => { setSelectedId(text(row.id)); setLastResult(null); }} type="button">
                <div className="flex items-start justify-between gap-4"><div><p className="font-black text-slate-950">{text(row.business_name || row.business?.name, "Commerce")}</p><p className="mt-1 text-xs font-semibold text-slate-500">{text(row.contact_name, "Contact")} · {text(row.contact_email, "Email absent")}</p><p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-emerald-700">{text(row.region || row.business?.region, "Région —")}{row.prp ? ` · PRP ${text(row.prp?.full_name || row.prp?.email)}` : ""}</p></div><span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase text-slate-700">{statusOf(row)}</span></div>
              </button>
            ))}</div>
          )}
        </div>

        <aside className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          {!selected ? <div className="flex min-h-96 items-center justify-center text-center"><div><p className="text-lg font-black text-slate-900">Dossier commerce</p><p className="mt-2 text-sm font-semibold text-slate-400">Sélectionne une demande.</p></div></div> : (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">Validation finale</p>
              <h3 className="mt-1 text-xl font-black text-slate-950">{text(selected.business_name || selected.business?.name, "Commerce")}</h3>
              <div className="mt-4 grid gap-2">
                <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Propriétaire</p><p className="mt-1 text-sm font-bold text-slate-800">{text(selected.profile?.full_name, "Nom non renseigné")}</p><p className="text-xs font-semibold text-slate-500">{text(selected.contact_email || selected.profile?.email, "Email absent")}</p></div>
                <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Commerce</p><p className="mt-1 text-sm font-semibold text-slate-700">Catégorie : {text(selected.category || selected.business?.category, "—")}</p><p className="text-sm font-semibold text-slate-700">Région : {text(selected.region || selected.business?.region, "—")}</p><p className="text-sm font-semibold text-slate-700">Téléphone : {text(selected.contact_phone, "—")}</p></div>
                {selected.linked_lead && <div className="rounded-xl bg-indigo-50 p-3"><p className="text-[10px] font-black uppercase text-indigo-500">Boucle PRP liée</p><p className="mt-1 text-sm font-bold text-indigo-950">{text(selected.linked_lead?.business_name, "Prospect")}</p><p className="text-xs font-semibold text-indigo-700">Pipeline : {text(selected.linked_lead?.pipeline_stage || selected.linked_lead?.status, "—")}</p></div>}
                <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Identité</p><p className="mt-1 text-sm font-semibold text-slate-700">Rôle : {text(selected.profile?.canonical_role, "user")}</p><p className="text-sm font-semibold text-slate-700">Onboarding : {text(selected.profile?.onboarding_status, "—")}</p></div>
              </div>
              <textarea className="mt-4 min-h-24 w-full rounded-xl border border-slate-200 p-3 text-sm font-semibold" placeholder="Motif obligatoire pour refus ou suspension." value={reason} onChange={(event) => setReason(event.target.value)} />
              <div className="mt-3 grid gap-2">
                {statusOf(selected) !== "approved" && <button className="rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white disabled:opacity-40" disabled={Boolean(busy)} onClick={() => void review("approve")} type="button">{busy === "approve" ? "Activation…" : "✓ Approuver et activer le portail"}</button>}
                {statusOf(selected) === "approved" && <button className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-black text-orange-800 disabled:opacity-40" disabled={Boolean(busy)} onClick={() => void review("suspend")} type="button">{busy === "suspend" ? "Suspension…" : "Suspendre le portail commerçant"}</button>}
                {statusOf(selected) !== "approved" && <button className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-black text-red-700 disabled:opacity-40" disabled={Boolean(busy)} onClick={() => void review("reject")} type="button">{busy === "reject" ? "Refus…" : "Refuser la demande"}</button>}
              </div>
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold leading-5 text-amber-900">Cette activation concerne uniquement l’identité commerçante et le portail Explore. Aucun paiement, abonnement, commission ou payout Giftshop n’est activé.</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
